/* ============================================================================
   THE MYSTERY GIFT — the scratch foil.  —  assets/scratch-card.js
   Loaded with `defer` from layout/theme.liquid. Markup: snippets/scratch-card.liquid.
   Styles: brand.css.

   Same foil on the cart page and in the cart drawer. The drawer rebuilds its copy
   from Liquid after every add / quantity change / remove, so this exposes
   `window.tpcScratch.init(scope)` and `window.tpcScratch.resize(scope)` and
   assets/overlays.js calls them after each re-render — a per-element binding done
   once at load would be thrown away with the old markup.

   EVERY GUARD HERE EXISTS SO A FAILURE LEAVES THE GIFT VISIBLE, NOT HIDDEN:
     - the prize is normal DOM and the canvas is painted after it, so with JS off,
       blocked or broken the canvas simply never covers anything
     - a missing 2d context returns before the foil is drawn at all
     - a blocked getImageData reports a fully-scratched card and opens it, rather
       than trapping the reader behind a surface that can never be cleared
   ============================================================================ */
(function () {
  'use strict';

  function build(root) {
    if (root.__tpcScratch) return root.__tpcScratch;

    var stage = root.querySelector('[data-scratch-stage]');
    var canvas = root.querySelector('[data-scratch-canvas]');
    if (!stage || !canvas || !canvas.getContext) return null;
    var ctx = canvas.getContext('2d');
    if (!ctx) return null;

    /* THE REVEAL RESETS, ON PURPOSE. This once remembered a completed scratch in localStorage, so
       every later visit skipped the foil and the gift sat there already open — which is exactly how
       it looked on a phone while a fresh browser profile looked correct. The scratch IS the gift, so
       the state is stored nowhere: every render starts with the foil on. (23 Sep) */
    var revealed = false;
    var moves = 0;
    var drawing = false;
    var last = null;
    var brush = 40;
    var sized = false;

    function open() {
      if (revealed) return;
      revealed = true;
      root.classList.add('is-open');
    }

    function paint(w, h) {
      var g = ctx.createLinearGradient(0, 0, w, h);
      g.addColorStop(0, '#7A5BC0');
      g.addColorStop(0.5, '#5A3C97');
      g.addColorStop(1, '#501C7B');
      ctx.globalCompositeOperation = 'source-over';
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, w, h);
      /* a faint speckle, so the foil reads as a surface rather than a flat block */
      ctx.fillStyle = 'rgba(255,255,255,.16)';
      for (var i = 0; i < 90; i++) {
        var x = (i * 97 % 100) / 100 * w, y = (i * 61 % 100) / 100 * h;
        ctx.fillRect(x, y, 2, 2);
      }
    }

    function size() {
      var r = stage.getBoundingClientRect();
      /* Not laid out yet (zero height, or a container that is still display:none). Painting a 1px
         foil and calling it done would leave a card that can never be scratched, so this says so and
         lets resize() pick it up once the box is real. */
      if (r.width < 2 || r.height < 2) { sized = false; return false; }
      var dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width  = Math.max(1, Math.round(r.width  * dpr));
      canvas.height = Math.max(1, Math.round(r.height * dpr));
      canvas.style.width = '100%'; canvas.style.height = '100%';
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      brush = Math.max(34, Math.round(r.width / 7));
      paint(r.width, r.height);
      sized = true;
      return true;
    }

    function at(e) {
      var r = canvas.getBoundingClientRect();
      return { x: e.clientX - r.left, y: e.clientY - r.top };
    }

    function dig(a, b) {
      ctx.globalCompositeOperation = 'destination-out';
      ctx.lineWidth = brush; ctx.lineCap = 'round'; ctx.lineJoin = 'round';
      ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.stroke();
      ctx.beginPath(); ctx.arc(b.x, b.y, brush / 2, 0, Math.PI * 2); ctx.fill();
    }

    function cleared() {
      try {
        var d = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
        var clear = 0, total = 0;
        for (var i = 3; i < d.length; i += 96) { total++; if (d[i] < 40) clear++; }
        return total ? clear / total : 1;
      } catch (e) { return 1; }
    }

    stage.addEventListener('pointerdown', function (e) {
      if (!sized && !size()) return;
      drawing = true; last = at(e);
      if (stage.setPointerCapture) { try { stage.setPointerCapture(e.pointerId); } catch (err) {} }
      dig(last, { x: last.x + 0.5, y: last.y + 0.5 });
      e.preventDefault();
    });
    stage.addEventListener('pointermove', function (e) {
      if (!drawing) return;
      var p = at(e); dig(last, p); last = p;
      if (++moves % 5 === 0 && cleared() > 0.42) open();
      e.preventDefault();
    });
    function stop() { if (!drawing) return; drawing = false; if (cleared() > 0.42) open(); }
    stage.addEventListener('pointerup', stop);
    stage.addEventListener('pointercancel', stop);
    stage.addEventListener('pointerleave', stop);
    stage.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') { e.preventDefault(); open(); }
    });

    size();

    /* iOS can run the sizing pass before the cart's fonts and layout have settled, which measures a
       stage box that is not final yet and leaves the foil mis-sized. One re-size after the page has
       fully loaded fixes that. It only runs while nothing has been scratched, so it can never wipe
       progress someone has already made. (23 Sep) */
    window.addEventListener('load', function () { if (!revealed && moves === 0) size(); });

    root.__tpcScratch = {
      open: open,
      size: size,
      isRevealed: function () { return revealed; },
      untouched: function () { return moves === 0; }
    };
    return root.__tpcScratch;
  }

  function each(scope, fn) {
    var list = (scope || document).querySelectorAll('[data-scratch]');
    for (var i = 0; i < list.length; i++) fn(list[i]);
  }

  window.tpcScratch = {
    /* build every card in `scope` that has not been built yet */
    init: function (scope) { each(scope, build); },
    /* re-measure cards that have not been touched — call this once the container is on screen */
    resize: function (scope) {
      each(scope, function (root) {
        var api = root.__tpcScratch;
        if (api && !api.isRevealed() && api.untouched()) api.size();
      });
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { window.tpcScratch.init(document); });
  } else {
    window.tpcScratch.init(document);
  }
})();
